import { forwardRef, useEffect, useRef, useState } from "react";

import { MagnifyingGlassIcon } from "@eisox/icons";

import { TextField } from "../TextField";

interface SearchFieldProps extends Omit<TextField.TextFieldProps, "value"> {
  value?: string;
  onValueChange?: (value: string) => void;
  defaultValue?: string;
  timeout?: number;
  rounded?: boolean;
  iconSide?: "left" | "right";
}

const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    { value, onValueChange, defaultValue, timeout = 500, rounded = false, iconSide = "left", children, ...props },
    forwardedRef,
  ) => {
    const isControlled = value !== undefined;

    const [inputValue, setInputValue] = useState(defaultValue ?? "");

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
      if (isControlled) {
        setInputValue(value);
      }
    }, [value, isControlled]);

    const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = event => {
      const newValue = event.target.value;

      setInputValue(newValue);

      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

      debounceTimeout.current = setTimeout(() => {
        onValueChange?.(newValue);
      }, timeout);
    };

    useEffect(() => {
      return () => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      };
    }, []);

    return (
      <TextField.Root {...props} ref={forwardedRef} onChange={handleInputChange} value={inputValue} rounded={rounded}>
        {children ?? (
          <TextField.Slot side={iconSide}>
            <MagnifyingGlassIcon />
          </TextField.Slot>
        )}
      </TextField.Root>
    );
  },
);

SearchField.displayName = "SearchField";

export { SearchField };
export type { SearchFieldProps };
